/**
 * time
 *
 * Shared helpers for working with timezones. `TIMEZONES` is the canonical
 * map from our short location keys (nyc, sf, ldn) to IANA zone names — add
 * to it as new offices/locations get used in the UI.
 *
 * `getTimeInZone` returns the current hour/minute/second in a given zone,
 * accepting either a short key or a raw IANA name. DST is handled
 * automatically via `Intl.DateTimeFormat`.
 */

export const TIMEZONES = {
  nyc: "America/New_York",
  sf: "America/Los_Angeles",
  ldn: "Europe/London",
} as const;

export type TimezoneKey = keyof typeof TIMEZONES;

export type TimeParts = {
  hour: number; // 0-23
  minute: number; // 0-59
  second: number; // 0-59 (integer)
  ms: number; // 0-999 — current millisecond within the second
  tzAbbr: string; // DST-aware short name, e.g. "EST" / "EDT" / "GMT" / "BST"
};

/** Fixed 3-letter labels for UI (avoids Intl values like "GMT+1"). */
const OFFICE_TZ_ABBR: Record<
  TimezoneKey,
  { standard: string; daylight: string }
> = {
  nyc: { standard: "EST", daylight: "EDT" },
  sf: { standard: "PST", daylight: "PDT" },
  ldn: { standard: "GMT", daylight: "BST" },
};

/** Resolve a short key (e.g. "nyc") to an IANA zone name, or pass through. */
export function resolveTimezone(zone: string): string {
  return (TIMEZONES as Record<string, string>)[zone] ?? zone;
}

// Formatter construction is expensive; cache one per zone so per-frame
// callers (Raf-driven clocks, etc.) pay only for `formatToParts`.
const formatters = new Map<string, Intl.DateTimeFormat>();

function getFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
      // Pull the DST-aware short name from the same formatter so callers
      // get e.g. "EST" in winter, "EDT" in summer without extra work.
      timeZoneName: "short",
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/**
 * Current local time in a given zone (accepts short key or IANA name).
 * `second` is integer; combine with `ms / 1000` for sub-second precision.
 */
export function getTimeInZone(zone: string, now: Date = new Date()): TimeParts {
  const parts = getFormatter(resolveTimezone(zone)).formatToParts(now);

  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  const getStr = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? "";

  // Some browsers return 24 for midnight under hour12:false. Normalize.
  let hour = get("hour");
  if (hour === 24) hour = 0;

  return {
    hour,
    minute: get("minute"),
    second: get("second"),
    ms: now.getMilliseconds(),
    tzAbbr: getStr("timeZoneName"),
  };
}

function getUtcOffsetMinutes(iana: string, when: Date): number {
  const utc = new Date(when.toLocaleString("en-US", { timeZone: "UTC" }));
  const local = new Date(when.toLocaleString("en-US", { timeZone: iana }));
  return (local.getTime() - utc.getTime()) / 60_000;
}

function isDaylightTime(iana: string, when: Date): boolean {
  const year = when.getFullYear();
  const winter = getUtcOffsetMinutes(iana, new Date(year, 0, 15));
  const summer = getUtcOffsetMinutes(iana, new Date(year, 6, 15));
  const now = getUtcOffsetMinutes(iana, when);
  if (winter === summer) return false;
  return now === Math.max(winter, summer);
}

/**
 * Three-letter zone label for office keys (nyc / sf / ldn). Falls back to the
 * first three characters of Intl's `timeZoneName` for unknown zones.
 */
export function getDisplayTzAbbr(zone: string, when: Date = new Date()): string {
  const key = zone.toLowerCase() as TimezoneKey;
  const office = OFFICE_TZ_ABBR[key];
  if (!office) {
    const raw = getTimeInZone(zone, when).tzAbbr;
    return raw.length <= 3 ? raw : raw.slice(0, 3);
  }
  const iana = TIMEZONES[key];
  return isDaylightTime(iana, when) ? office.daylight : office.standard;
}

/** Every abbreviation `getDisplayTzAbbr` may return for an office key. */
export function getOfficeTzAbbrs(zone: string): string[] {
  const key = zone.toLowerCase() as TimezoneKey;
  const office = OFFICE_TZ_ABBR[key];
  if (!office) return [getDisplayTzAbbr(zone)];
  return [office.standard, office.daylight];
}
